import { useEffect, useMemo, useRef, useState } from "react";
import type { CalculationResult } from "../../engines/types";
import {
	advanceMilestoneLatch,
	complianceMilestoneState,
	createMilestoneLatch,
	deriveComplianceStatus,
	type MilestoneState,
	resyncMilestoneLatch,
} from "./milestoneModel";

/**
 * How long a celebration stays on screen. Matched to the badge settle keyframe
 * in BuilderPage.css (2.4s) so the badge never stands statically lit after its
 * animation has finished. Purely a DOM/CSS concern - nothing here touches the
 * 3D canvas or requests a frame from the demand loop.
 */
export const MILESTONE_CELEBRATION_MS = 2400;

interface MilestoneFeedbackInput {
	/** The already-memoized `calculateMaterials()` result. */
	calcData: Partial<CalculationResult>;
	/** True when every tracked design decision is set. */
	completenessComplete: boolean;
	/**
	 * False while the design is still resolving (a saved design or a ?share=
	 * link loading). The hook is called above BuilderPage's loading early
	 * return, so without this the first observation is always the blank seed
	 * and a fully-complete saved design would read as a fresh achievement.
	 */
	settled?: boolean;
	/**
	 * Bump to re-baseline both latches - the design was replaced rather than
	 * edited (a template applied). The next observation lands on `unknown`,
	 * which never celebrates.
	 */
	resyncToken?: number;
}

export interface MilestoneFeedback {
	/** True for a short window after every rule check first comes back clear. */
	rulesCelebrating: boolean;
	/** True for a short window after the design first becomes complete. */
	completenessCelebrating: boolean;
}

/**
 * Session-scoped milestone feedback for the builder.
 *
 * Derived state lives in refs + local component state, never in the Zustand
 * store (the store holds draft state only) and never in the database. The
 * transition rules themselves are the pure `milestoneModel` functions, so the
 * "never on mount / never twice" contract is unit-testable without a DOM.
 */
export function useMilestoneFeedback({
	calcData,
	completenessComplete,
	settled = true,
	resyncToken = 0,
}: MilestoneFeedbackInput): MilestoneFeedback {
	const complianceStatus = useMemo(
		() => deriveComplianceStatus(calcData).status,
		[calcData],
	);

	const rulesLatchRef = useRef(createMilestoneLatch());
	const completenessLatchRef = useRef(createMilestoneLatch());
	const [rulesCelebrating, setRulesCelebrating] = useState(false);
	const [completenessCelebrating, setCompletenessCelebrating] = useState(false);

	// Declared first so it runs before the two observation effects in the same
	// commit: a resync always re-baselines before the new state is read. The
	// token is compared rather than merely listed as a dependency, so the mount
	// run is a no-op and the effect is honestly keyed on the value it reads.
	const lastResyncRef = useRef(resyncToken);
	useEffect(() => {
		if (lastResyncRef.current === resyncToken) return;
		lastResyncRef.current = resyncToken;
		rulesLatchRef.current = resyncMilestoneLatch(rulesLatchRef.current);
		completenessLatchRef.current = resyncMilestoneLatch(
			completenessLatchRef.current,
		);
	}, [resyncToken]);

	const rulesState: MilestoneState = settled
		? complianceMilestoneState(complianceStatus)
		: "unknown";
	const completenessState: MilestoneState = settled
		? completenessComplete
			? "reached"
			: "pending"
		: "unknown";

	useEffect(() => {
		const { latch, celebrate } = advanceMilestoneLatch(
			rulesLatchRef.current,
			rulesState,
		);
		rulesLatchRef.current = latch;
		if (!celebrate) return;

		setRulesCelebrating(true);
		const timer = setTimeout(
			() => setRulesCelebrating(false),
			MILESTONE_CELEBRATION_MS,
		);
		// Clearing the timer is not enough: when the status changes inside the
		// window this effect re-runs and early-returns, so the cleanup owns
		// putting the flag back down. Otherwise it stays true for the session.
		return () => {
			clearTimeout(timer);
			setRulesCelebrating(false);
		};
	}, [rulesState]);

	useEffect(() => {
		const { latch, celebrate } = advanceMilestoneLatch(
			completenessLatchRef.current,
			completenessState,
		);
		completenessLatchRef.current = latch;
		if (!celebrate) return;

		setCompletenessCelebrating(true);
		const timer = setTimeout(
			() => setCompletenessCelebrating(false),
			MILESTONE_CELEBRATION_MS,
		);
		return () => {
			clearTimeout(timer);
			setCompletenessCelebrating(false);
		};
	}, [completenessState]);

	return { rulesCelebrating, completenessCelebrating };
}
